import React from "react";
import {
  Card,
  Box,
  Typography,
  Grid,
  Divider,
  Chip,
  Stack,
} from "@mui/material";
import { Login, Logout, Coffee } from "@mui/icons-material";

const AttendanceCard = ({ title = "Daily Attendance", attendance }) => {
  const status = attendance?.status;

  // Dynamic color for status
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "ontime":
        return "success";
      case "absent":
        return "error";
      case "late":
        return "warning";
      default:
        return "default";
    }
  };

  // Collect session_* keys, sorted by session number
  const sessions = Object.keys(attendance || {})
    .filter((key) => key.startsWith("session_"))
    .map((key) => attendance[key])
    .sort((a, b) => a.sessionNumber - b.sessionNumber);

  return (
    <Card
      sx={{
        border: "1.5px solid #2E7D32",
        borderRadius: "16px",
        boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
        p: 3,
        bgcolor: "#fff",
        height: "100%",
      }}
    >
      {/* Header */}
      <Box textAlign="center" mb={2}>
        <Typography variant="h6" fontWeight={600} color="text.primary">
          {title}
        </Typography>
        {status && (
          <Chip
            label={status}
            color={getStatusColor(status)}
            size="small"
            sx={{ mt: 1 }}
          />
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {sessions.length === 0 ? (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No attendance
        </Typography>
      ) : (
        sessions.map((session, index) => (
          <Box key={session.sessionNumber}>
            {index > 0 && <Divider sx={{ my: 2 }} />}

            {/* Session Number Header if needed or just display times
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              mb={1.5}
            >
              Session {session.sessionNumber}
            </Typography> */}

            {/* Times Grid */}
            <Grid container spacing={2}>
              <Grid item xs={6} textAlign="center">
                <Login color="success" />
                <Typography variant="body2" color="text.secondary">
                  Check In
                </Typography>
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  {session.checkIn?.time || "--"}
                </Typography>
              </Grid>
              <Grid item xs={6} textAlign="center">
                <Logout color="error" />
                <Typography variant="body2" color="text.secondary">
                  Check Out
                </Typography>
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  {session.checkOut?.time || "--"}
                </Typography>
              </Grid>
            </Grid>

            {/* Multiple Breaks Support */}
            {session.breaks && session.breaks.length > 0 ? (
              <Box mt={2}>
                <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={1}>
                  Breaks:
                </Typography>
                <Stack spacing={1}>
                  {session.breaks.map((brk, bIdx) => (
                    <Box
                      key={bIdx}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        bgcolor: "#f9f9f9",
                        p: 1,
                        borderRadius: "8px",
                        border: "1px solid #eee",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Coffee sx={{ color: brk.type === "breakIn" ? "#ff9800" : "#6d4c41", fontSize: "1.2rem" }} />
                        <Typography variant="body2" fontWeight={500}>
                          {brk.type === "breakIn" ? "Break In" : "Break Out"}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        {brk.time || "--"}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ) : (
              // Fallback for legacy single breakIn/breakOut objects if any
              (session.breakIn || session.breakOut) && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6} textAlign="center">
                    <Coffee sx={{ color: "#ff9800" }} />
                    <Typography variant="body2" color="text.secondary">
                      Break In
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="text.primary">
                      {session.breakIn?.time || "--"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="center">
                    <Coffee sx={{ color: "#6d4c41" }} />
                    <Typography variant="body2" color="text.secondary">
                      Break Out
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="text.primary">
                      {session.breakOut?.time || "--"}
                    </Typography>
                  </Grid>
                </Grid>
              )
            )}
          </Box>
        ))
      )}
    </Card>
  );
};

export default AttendanceCard;
